<template>
    <div class="tab-note">
        <div v-if="localList.length > 0" class="content">
            <!-- Top Pagination -->
            <div v-if="totalPages > 1" class="pagination-container pagination-top">
                <button class="pagination-btn" :disabled="currentPage === 1" @click="currentPage = 1">
                    &laquo;
                </button>
                <button class="pagination-btn" :disabled="currentPage === 1" @click="currentPage--">
                    &lt;
                </button>

                <button
                    v-for="page in visiblePages"
                    :key="`top-${page}`"
                    class="pagination-btn"
                    :class="{ active: page === currentPage }"
                    @click="currentPage = page"
                >
                    {{ page }}
                </button>

                <button class="pagination-btn" :disabled="currentPage === totalPages" @click="currentPage++">
                    &gt;
                </button>
                <button class="pagination-btn" :disabled="currentPage === totalPages" @click="currentPage = totalPages">
                    &raquo;
                </button>
            </div>

            <!-- Notes Stack -->
            <div
                v-for="(note, index) in paginatedList"
                :key="note.id || index"
                class="card-note"
            >
                <!-- Card Header -->
                <div class="card-note-header" @click="toggleNote(note.id)">
                    <div class="profile-info">
                        <div class="picture">
                            <img
                                v-if="note.photo || note.userPhotoUrl"
                                :src="note.photo || note.userPhotoUrl"
                                alt="User photo"
                            />
                            <AvatarPlaceholder
                                v-else
                                size="46px"
                                :text="avatarText(note.firstName, note.lastName)"
                            />
                        </div>
                        <div class="user-meta">
                            <div class="name">
                                {{ `${note.firstName || ""} ${note.lastName || ""}`.trim() || "-" }}
                            </div>
                            <div class="sub-meta">
                                <span class="appointment-id">ID: <strong>{{ note.id }}</strong></span>
                                <template v-if="note.resource">
                                    <span class="meta-separator">•</span>
                                    <span class="resource-name">{{ note.resource }}</span>
                                </template>
                                <template v-if="note.shift || note.turnoName">
                                    <span class="meta-separator">•</span>
                                    <span class="shift-name">{{ note.shift || note.turnoName }}</span>
                                </template>
                            </div>
                        </div>
                    </div>

                    <div class="header-right">
                        <!-- Date Badge -->
                        <div class="date-badge">
                            <span class="date-text">{{ formatDate(note.eventTime) }}</span>
                        </div>

                        <!-- Type Pill (Only displayed if rework) -->
                        <div
                            v-if="note.isRework || note.is_rework == 1"
                            class="type-pill rework-pill"
                        >
                            Retrabalho
                        </div>

                        <!-- Status Pill -->
                        <div
                            v-if="note.statusName"
                            class="status-pill"
                            :style="getStatusStyle(note.statusColor)"
                        >
                            {{ note.statusName }}
                        </div>

                        <!-- Delete Button (Large touch target for gloves) -->
                        <button
                            type="button"
                            class="btn-delete"
                            :title="$t('ResourceStatus.Delete') || 'Excluir apontamento'"
                            @click.stop="removeNote(note.id)"
                        >
                            <feather-icon icon="Trash2Icon" size="22" />
                        </button>

                        <!-- Expand Chevron (Large touch target for gloves) -->
                        <button
                            type="button"
                            class="btn-expand"
                            :class="{ open: isNoteOpen(note.id) }"
                            :aria-label="isNoteOpen(note.id) ? 'Recolher' : 'Expandir'"
                            @click.stop="toggleNote(note.id)"
                        >
                            <feather-icon icon="ChevronDownIcon" size="24" />
                        </button>
                    </div>
                </div>

                <!-- Quantities Summary Bar with Big High-Visibility Numbers -->
                <div class="quantities-summary-bar">
                    <div class="qty-item">
                        <span class="qty-label">{{ $t("OperationProgress.Produced") }}</span>
                        <span class="qty-value produced">
                            {{ formatCurrencyWithoutSymbol(note.producedQuantity || 0) }}
                        </span>
                    </div>

                    <div class="qty-item">
                        <span class="qty-label">{{ $t("Scrap") }}</span>
                        <span class="qty-value scrap">
                            {{ formatCurrencyWithoutSymbol(note.refuseQuantity || 0) }}
                        </span>
                    </div>

                    <div class="qty-item">
                        <span class="qty-label">{{ $t("ProductionProgress.AppointmentModal.Card.Rework") }}</span>
                        <span class="qty-value rework">
                            {{ formatCurrencyWithoutSymbol(note.reworkQuantity || 0) }}
                        </span>
                    </div>

                    <div v-if="note.parameters && note.parameters.length" class="qty-item params-badge-item">
                        <span class="params-badge">
                            <feather-icon icon="SlidersIcon" size="15" class="mr-25" />
                            {{ note.parameters.length }} {{ note.parameters.length === 1 ? 'parâmetro' : 'parâmetros' }}
                        </span>
                    </div>
                </div>

                <!-- Expandable Details -->
                <b-collapse :visible="isNoteOpen(note.id)" class="details-collapse">
                    <div class="details-body">
                        <!-- Extra Meta Grid -->
                        <div class="details-grid">
                            <div class="detail-item" v-if="note.resource">
                                <span class="detail-label">{{ $t("ordersList.Resource") }}</span>
                                <span class="detail-value">{{ note.resource }}</span>
                            </div>

                            <div class="detail-item" v-if="note.stopReason || note.reason">
                                <span class="detail-label">Motivo</span>
                                <span class="detail-value">{{ note.stopReason || note.reason }}</span>
                            </div>

                            <div class="detail-item" v-if="note.startTime || note.start_time">
                                <span class="detail-label">Início</span>
                                <span class="detail-value">{{ formatDate(note.startTime || note.start_time) }}</span>
                            </div>

                            <div class="detail-item" v-if="note.endTime || note.end_time">
                                <span class="detail-label">Fim</span>
                                <span class="detail-value">{{ formatDate(note.endTime || note.end_time) }}</span>
                            </div>
                        </div>

                        <!-- Custom Parameters (Styled cleanly with larger text) -->
                        <div v-if="note.parameters && note.parameters.length" class="custom-parameters-section">
                            <div class="params-section-header">
                                <span class="title-text">{{ $t("Operator.CustomParameters") }}</span>
                                <span class="params-count">({{ note.parameters.length }})</span>
                            </div>

                            <div class="params-grid">
                                <div
                                    v-for="(param, pIdx) in note.parameters"
                                    :key="pIdx"
                                    class="param-item"
                                >
                                    <div class="param-label-row">
                                        <span class="param-label">{{ param.parameterLabel || param.parameterName }}</span>
                                        <span
                                            v-if="param.parameterLabel && param.parameterName && param.parameterLabel !== param.parameterName"
                                            class="param-tech-name"
                                        >
                                            ({{ param.parameterName }})
                                        </span>
                                    </div>
                                    <div class="param-value-container">
                                        <span class="param-value">{{ param.displayValue || param.rawValue }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </b-collapse>
            </div>

            <!-- Bottom Pagination -->
            <div v-if="totalPages > 1" class="pagination-container pagination-bottom">
                <button class="pagination-btn" :disabled="currentPage === 1" @click="currentPage = 1">
                    &laquo;
                </button>
                <button class="pagination-btn" :disabled="currentPage === 1" @click="currentPage--">
                    &lt;
                </button>

                <button
                    v-for="page in visiblePages"
                    :key="`bottom-${page}`"
                    class="pagination-btn"
                    :class="{ active: page === currentPage }"
                    @click="currentPage = page"
                >
                    {{ page }}
                </button>

                <button class="pagination-btn" :disabled="currentPage === totalPages" @click="currentPage++">
                    &gt;
                </button>
                <button class="pagination-btn" :disabled="currentPage === totalPages" @click="currentPage = totalPages">
                    &raquo;
                </button>
            </div>
        </div>

        <NotFound v-else />
    </div>
</template>

<script>
import { avatarText } from "@core/utils/filter";
import { BCollapse } from "bootstrap-vue";
import { mapActions, mapMutations } from "vuex";
import { formatCurrencyWithoutSymbol } from "@/views/pages/operator/utils";
import AvatarPlaceholder from "@core/components/avatar-placeholder/index.vue";
import FeatherIcon from "@core/components/feather-icon/FeatherIcon.vue";
import moment from "moment";

export default {
    name: "TabNote",
    components: {
        NotFound: () => import("../NotFound.vue"),
        AvatarPlaceholder,
        FeatherIcon,
        BCollapse
    },
    props: {
        list: {
            type: Array,
            default: () => []
        },
        isMultipleOrders: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            formatCurrencyWithoutSymbol,
            localList: [],
            openedNotes: {},
            language: this.$cookies.get("userInfo")?.language,
            currentSite: this.$cookies.get("userInfo")?.currentSite,
            itemsPerPage: 50,
            currentPage: 1
        };
    },
    watch: {
        list: {
            immediate: true,
            handler(value) {
                this.localList = value || [];
                this.currentPage = 1;
            }
        }
    },
    computed: {
        totalPages() {
            return Math.ceil(this.localList.length / this.itemsPerPage) || 1;
        },
        paginatedList() {
            const start = (this.currentPage - 1) * this.itemsPerPage;
            return this.localList.slice(start, start + this.itemsPerPage);
        },
        visiblePages() {
            const pages = [];
            const maxVisible = 5;
            let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
            let end = Math.min(this.totalPages, start + maxVisible - 1);

            if (end - start < maxVisible - 1) {
                start = Math.max(1, end - maxVisible + 1);
            }

            for (let i = start; i <= end; i++) {
                pages.push(i);
            }

            return pages;
        }
    },
    methods: {
        ...mapMutations("app", ["UPDATE_FLAG_SHOW_OVERLAY"]),
        ...mapActions("app", ["removeRequestLoading"]),
        avatarText,
        toggleNote(id) {
            this.$set(this.openedNotes, id, !this.openedNotes[id]);
        },
        isNoteOpen(id) {
            return !!this.openedNotes[id];
        },
        formatDate(date) {
            if (!date) return "-";
            if (this.language === "pt-BR") {
                return moment(date).format("DD/MM/YYYY HH:mm");
            }
            return moment(date).format("MM/DD/YYYY HH:mm");
        },
        getStatusStyle(color) {
            const c = color || "#974900";
            return {
                backgroundColor: `${c}18`,
                color: c,
                borderColor: `${c}40`
            };
        },
        removeNote(operationId) {
            this.$swal({
                title: this.$t("IntegrationPage.attention"),
                text: this.$t("Operator.RemoveNoteAlert"),
                showCancelButton: true,
                showCloseButton: true,
                cancelButtonText: this.$t("MySites.cancel"),
                confirmButtonText: this.$t("ResourceStatus.Delete"),
                customClass: {
                    container: "swal-default",
                    confirmButton: "btn btn-primary",
                    cancelButton: "btn btn-outline-danger ml-1"
                },
                buttonsStyling: false
            }).then((result) => {
                if (result.value) {
                    this.UPDATE_FLAG_SHOW_OVERLAY(true);

                    this.$http2
                        .delete(
                            `/api/mysfc/operation-progress/${operationId}?siteId=${this.currentSite}`
                        )
                        .then(() => {
                            this.$emit("deleteOperationProgress");
                        })
                        .catch((error) => {
                            this.showToast(
                                "Error!",
                                "FrownIcon",
                                error?.response?.data?.message || "Erro ao excluir",
                                "danger"
                            );
                            this.removeRequestLoading();
                        });
                }
            });
        }
    }
};
</script>

<style lang="scss" scoped>
.tab-note {
    flex-grow: 1;
    width: 100%;

    .content {
        padding: 24px 29px 40px;
        display: flex;
        flex-direction: column;
        gap: 20px;
    }

    /* Card styling */
    .card-note {
        border-radius: 16px;
        border: 1px solid #cfc4be;
        background: #ffffff;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
        overflow: hidden;
        transition: box-shadow 0.2s ease, border-color 0.2s ease;

        &:hover {
            box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08);
            border-color: #b5a9a1;
        }

        /* Card Header */
        .card-note-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 16px 24px;
            background-color: #faf9f8;
            border-bottom: 1px solid #ede8e3;
            flex-wrap: wrap;
            gap: 16px;
            cursor: pointer;
            user-select: none;

            .profile-info {
                display: flex;
                align-items: center;
                gap: 14px;

                .picture {
                    display: flex;
                    align-items: center;
                    justify-content: center;

                    img {
                        width: 46px;
                        height: 46px;
                        object-fit: cover;
                        border-radius: 50%;
                        border: 2px solid #ffffff;
                        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.12);
                    }
                }

                .user-meta {
                    display: flex;
                    flex-direction: column;
                    gap: 3px;

                    .name {
                        color: #2e2825;
                        font-size: 18px;
                        font-weight: 700;
                        line-height: 24px;
                    }

                    .sub-meta {
                        display: flex;
                        align-items: center;
                        gap: 8px;
                        font-size: 15px;
                        color: #6e645e;
                        font-weight: 500;
                        flex-wrap: wrap;

                        .appointment-id {
                            strong {
                                color: #2e2825;
                                font-weight: 700;
                            }
                        }

                        .meta-separator {
                            color: #b5aba4;
                            font-weight: 700;
                        }

                        .resource-name,
                        .shift-name {
                            color: #594f4a;
                            font-weight: 600;
                        }
                    }
                }
            }

            .header-right {
                display: flex;
                align-items: center;
                gap: 12px;
                flex-wrap: wrap;

                .date-badge {
                    font-size: 15px;
                    font-weight: 600;
                    color: #2e2825;
                    background-color: #ffffff;
                    padding: 8px 14px;
                    border-radius: 8px;
                    border: 1px solid #dcd3cb;
                }

                .type-pill {
                    display: inline-flex;
                    align-items: center;
                    padding: 8px 16px;
                    border-radius: 20px;
                    font-size: 14px;
                    font-weight: 700;
                    letter-spacing: 0.2px;

                    &.productive-pill {
                        background-color: #eaf8f0;
                        color: #1f9d55;
                        border: 1px solid rgba(31, 157, 85, 0.35);
                    }

                    &.rework-pill {
                        background-color: #fff4e6;
                        color: #e67e22;
                        border: 1px solid rgba(230, 126, 34, 0.35);
                    }
                }

                .status-pill {
                    display: inline-flex;
                    align-items: center;
                    padding: 8px 16px;
                    border-radius: 20px;
                    font-size: 14px;
                    font-weight: 700;
                    border: 1px solid transparent;
                    white-space: nowrap;
                    letter-spacing: 0.2px;
                }

                /* Glove-friendly Delete Button (44px x 44px touch target) */
                .btn-delete {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    width: 44px;
                    height: 44px;
                    padding: 0 !important;
                    margin: 0;
                    box-sizing: border-box;
                    border-radius: 8px;
                    border: 1px solid #fca5a5;
                    background-color: #ffffff;
                    cursor: pointer;
                    color: #ea5455;
                    transition: all 0.2s ease;

                    svg,
                    .feather {
                        width: 22px;
                        height: 22px;
                        display: block;
                        margin: auto;
                        stroke: #ea5455;
                    }

                    &:hover {
                        background-color: #fee2e2;
                        border-color: #ea5455;
                        color: #d32f2f;

                        svg,
                        .feather {
                            stroke: #d32f2f;
                        }
                    }
                }

                /* Glove-friendly Expand Button (44px x 44px touch target) */
                .btn-expand {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    width: 44px;
                    height: 44px;
                    padding: 0 !important;
                    margin: 0;
                    box-sizing: border-box;
                    border-radius: 8px;
                    border: 1px solid #dcd3cb;
                    background-color: #ffffff;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    color: #594f4a;

                    svg,
                    .feather {
                        width: 24px;
                        height: 24px;
                        display: block;
                        margin: auto;
                        transition: transform 0.25s ease;
                    }

                    &.open svg,
                    &.open .feather {
                        transform: rotate(180deg);
                    }

                    &:hover {
                        background-color: #ffede2;
                        border-color: #974900;
                        color: #974900;
                    }
                }
            }
        }

        /* Quantities Summary Bar with Big High-Visibility Numbers */
        .quantities-summary-bar {
            display: flex;
            align-items: center;
            gap: 32px;
            padding: 16px 24px;
            background-color: #ffffff;
            border-bottom: 1px solid #f2eee9;
            flex-wrap: wrap;

            .qty-item {
                display: flex;
                align-items: baseline;
                gap: 8px;

                .qty-label {
                    font-size: 14px;
                    color: #7d726b;
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                .qty-value {
                    font-size: 22px;
                    font-weight: 800;
                    line-height: 26px;
                    color: #2e2825;

                    &.produced {
                        color: #1f9d55;
                    }

                    &.scrap {
                        color: #d93b3b;
                    }

                    &.rework {
                        color: #e67e22;
                    }
                }

                &.params-badge-item {
                    margin-left: auto;

                    .params-badge {
                        display: inline-flex;
                        align-items: center;
                        gap: 6px;
                        padding: 6px 14px;
                        border-radius: 16px;
                        background-color: #ffede2;
                        color: #974900;
                        font-size: 14px;
                        font-weight: 700;
                    }
                }
            }
        }

        /* Expandable Details Collapse */
        .details-collapse {
            background-color: #faf9f8;
            border-top: 1px solid #ede8e3;

            .details-body {
                padding: 20px 24px 24px;
                display: flex;
                flex-direction: column;
                gap: 18px;

                /* Extra Meta Grid */
                .details-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
                    gap: 14px;
                    background-color: #ffffff;
                    border: 1px solid #e5dfd8;
                    border-radius: 10px;
                    padding: 16px 20px;

                    .detail-item {
                        display: flex;
                        flex-direction: column;
                        gap: 4px;

                        .detail-label {
                            font-size: 13px;
                            color: #7d726b;
                            font-weight: 700;
                            text-transform: uppercase;
                            letter-spacing: 0.5px;
                        }

                        .detail-value {
                            font-size: 16px;
                            color: #2e2825;
                            font-weight: 600;
                        }
                    }
                }

                /* Custom Parameters */
                .custom-parameters-section {
                    display: flex;
                    flex-direction: column;
                    gap: 12px;

                    .params-section-header {
                        display: flex;
                        align-items: center;
                        gap: 8px;

                        .title-text {
                            font-size: 14px;
                            font-weight: 800;
                            color: #7d726b;
                            text-transform: uppercase;
                            letter-spacing: 0.5px;
                        }

                        .params-count {
                            font-size: 14px;
                            font-weight: 700;
                            color: #974900;
                        }
                    }

                    .params-grid {
                        display: flex;
                        flex-direction: column;
                        gap: 10px;

                        .param-item {
                            display: flex;
                            flex-direction: column;
                            background-color: #ffffff;
                            border: 1px solid #e5dfd8;
                            border-radius: 10px;
                            padding: 14px 18px;
                            transition: border-color 0.15s ease;

                            &:hover {
                                border-color: #ffd8b8;
                            }

                            .param-label-row {
                                display: flex;
                                align-items: center;
                                gap: 8px;
                                margin-bottom: 6px;

                                .param-label {
                                    font-size: 16px;
                                    font-weight: 700;
                                    color: #974900;
                                }

                                .param-tech-name {
                                    font-size: 13px;
                                    color: #8c827c;
                                }
                            }

                            .param-value-container {
                                .param-value {
                                    font-size: 17px;
                                    line-height: 24px;
                                    color: #2e2825;
                                    font-weight: 600;
                                    white-space: pre-wrap;
                                    word-break: break-word;
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    /* Glove-friendly Pagination Buttons (46px x 46px touch target) */
    .pagination-container {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 10px;
        flex-wrap: wrap;

        &.pagination-top {
            margin-bottom: 6px;
        }

        &.pagination-bottom {
            margin-top: 16px;
        }

        .pagination-btn {
            min-width: 46px;
            height: 46px;
            padding: 8px 14px;
            display: flex;
            align-items: center;
            justify-content: center;
            border: 1px solid #cfc4be;
            border-radius: 8px;
            background: #ffffff;
            color: #2e2825;
            font-weight: 700;
            font-size: 17px;
            cursor: pointer;
            transition: all 0.2s ease;

            &:hover:not(:disabled) {
                background: #ffede2;
                border-color: #974900;
                color: #974900;
            }

            &.active {
                background: #974900;
                color: #ffffff;
                border-color: #974900;
            }

            &:disabled {
                opacity: 0.35;
                cursor: not-allowed;
            }
        }
    }
}

@media (max-width: 600px) {
    .tab-note {
        .content {
            padding: 24px 16px 40px !important;
        }

        .card-note {
            .card-note-header {
                flex-direction: column;
                align-items: flex-start;

                .header-right {
                    width: 100%;
                    justify-content: space-between;
                }
            }

            .quantities-summary-bar {
                padding: 12px 16px;
                gap: 16px;

                .params-badge-item {
                    margin-left: 0;
                    width: 100%;
                }
            }

            .details-collapse {
                .details-body {
                    padding: 14px 16px;

                    .details-grid {
                        grid-template-columns: 1fr;
                    }
                }
            }
        }

        .pagination-container {
            gap: 6px;

            .pagination-btn {
                min-width: 38px;
                height: 38px;
                padding: 4px 8px;
                font-size: 15px;
            }
        }
    }
}
</style>
